import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Loader from '../../components/Common/Loader';
import { ArrowLeft, TrendingUp, Calendar, Building2 } from 'lucide-react';

interface OPCVMDetails {
    id: number;
    nom_fonds: string;
    type_fonds: string | null;
    societe_gestion: string | null;
    date_creation: string | null;
    valeur_origine: number | null;
    cotations: {
        date_cotation: string;
        valeur_liquidative: number | null;
        date_vl: string | null;
        variation_origine_pct: number | null;
    }[];
}

const OPCVMDetailsScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [details, setDetails] = useState<OPCVMDetails | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token && id) fetchDetails();
    }, [token, id]);

    const fetchDetails = async () => {
        try {
            const response = await fetch(`${API_URL}/v1/opcvm/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setDetails(data);
            }
        } catch (error) {
            console.error('Error fetching OPCVM details:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <SiteLayout><Loader message="Chargement..." /></SiteLayout>;
    if (!details) return <SiteLayout><div style={{ padding: '40px', textAlign: 'center' }}>OPCVM non trouvé</div></SiteLayout>;

    const latestVL = details.cotations[0]?.valeur_liquidative;
    const latestVar = details.cotations[0]?.variation_origine_pct;

    return (
        <SiteLayout>
            <Header title="Détails OPCVM" />
            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <button
                    onClick={() => navigate('/opcvm')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#f3f4f6', cursor: 'pointer' }}
                >
                    <ArrowLeft size={18} /> Retour à la liste
                </button>

                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '20px',
                    padding: '30px',
                    color: 'white',
                    marginBottom: '24px',
                }}>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{details.nom_fonds}</h1>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <TrendingUp size={20} />
                            <span>Catégorie: {details.type_fonds || 'N/A'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Building2 size={20} />
                            <span>{details.societe_gestion || 'N/A'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={20} />
                            <span>Créé le: {details.date_creation ? new Date(details.date_creation).toLocaleDateString('fr-FR') : 'N/A'}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', padding: '16px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>VL Actuelle</div>
                            <div style={{ fontSize: '28px', fontWeight: 700 }}>{latestVL?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Variation Origine</div>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: (latestVar ?? 0) >= 0 ? '#a7f3d0' : '#fecaca' }}>
                                {latestVar?.toFixed(2)}%
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Valeur Origine</div>
                            <div style={{ fontSize: '28px', fontWeight: 700 }}>{details.valeur_origine?.toLocaleString('fr-FR')}</div>
                        </div>
                    </div>
                </div>

                <div style={{ background: 'var(--bg-box)', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                    <h2 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Historique VL (dernières 20)</h2>
                    {details.cotations.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '12px', textAlign: 'right' }}>VL</th>
                                    <th style={{ padding: '12px', textAlign: 'right' }}>Var. Origine</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.cotations.slice(0, 20).map((c, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px' }}>{new Date(c.date_cotation).toLocaleDateString('fr-FR')}</td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>{c.valeur_liquidative?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', textAlign: 'right', color: (c.variation_origine_pct ?? 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                            {c.variation_origine_pct?.toFixed(2)}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Aucun historique disponible</div>
                    )}
                </div>
            </div>
        </SiteLayout>
    );
};

export default OPCVMDetailsScreen;

